import { REDACTED_VALUE, isSensitiveKey, redactForDisplay, redactedJson, redactSensitiveText } from '../lib/redaction';
import { createI18nNamespace, t, type LocaleCode } from '../i18n';
import type { StatusItem } from '../components/ResourceDetail';

export type ResourceSources = {
  errors?: Record<string, string>;
};

export type SkillLibrarySources = ResourceSources & {
  skills?: unknown;
  conflicts?: unknown;
};

export type SkillDetailSources = ResourceSources & {
  skill?: unknown;
  installations?: unknown;
  conflicts?: unknown;
  versions?: unknown;
  preview?: unknown;
  validation?: unknown;
  mutationResult?: unknown;
};

export type LoadedSkillsSources = ResourceSources & {
  personas?: unknown;
  personaDetails?: unknown[];
};

export type MCPSources = ResourceSources & {
  mcps?: unknown;
  conflicts?: unknown;
  personas?: unknown;
  personaDetails?: unknown[];
  presets?: unknown;
};

export type MCPDetailSources = ResourceSources & {
  mcp?: unknown;
  conflicts?: unknown;
  personaDetails?: unknown[];
  personas?: unknown;
  preview?: unknown;
  versions?: unknown;
  audit?: unknown;
};

export type MCPPresetSources = ResourceSources & {
  presets?: unknown;
};

export type ToolProxySources = ResourceSources & {
  personas?: unknown;
  personaDetails?: unknown[];
};

export type HeaderModel = {
  title: string;
  description: string;
  meta: Array<[string, string]>;
};

export type EvidenceState = 'configured' | 'runtime-active' | 'planned' | 'unknown' | 'partial';
export type MCPMode = 'native' | 'proxy';

const rmCopy = createI18nNamespace('modules.resourceManagement');

type ResourceModelOptions = {
  locale?: LocaleCode;
};

type ResourceManagementCopy = ReturnType<typeof createResourceManagementCopy>;

function createResourceManagementCopy(options: ResourceModelOptions = {}) {
  const rmText = (suffix: string) => t(rmCopy.key(suffix), options.locale ?? 'en-US');
  return {
    contractGap: rmText('controls.backendPreviewConfirmationGap'),
    runtimeNotInferred: rmText('evidence.runtimeNotInferred'),
    runtimeNotInferredStatus: rmText('evidence.runtimeNotInferredStatus'),
    runtimeAssociationNotice: rmText('evidence.runtimeAssociationNotice'),
    unavailableContractGap: rmText('controls.unavailableContractGap'),
    installContractGap: rmText('controls.installPreviewConfirmationGap'),
    presetInstallAvailable: rmText('controls.presetInstallAvailable'),
    presetInstallAction: rmText('controls.presetInstallAction'),
    mutationContractGap: rmText('controls.mutationPreviewConfirmationGap'),
    enableDisableInstallPublishGap: rmText('controls.enableDisableInstallPublishGap'),
    configuredRuntimeUnknown: rmText('evidence.configuredArtifactRuntimeUnknown'),
    loadedSkillsRuntimeOnly: rmText('evidence.loadedSkillsRuntimeOnly'),
    requiresRuntimeProfileEvidence: rmText('evidence.requiresRuntimeProfileEvidence'),
    mcpDefinitionRuntimeUnknown: rmText('evidence.mcpDefinitionRuntimeUnknown'),
    presetPlannedDescription: rmText('presets.plannedDescription'),
    evidenceStateLabel: rmText('labels.evidenceState'),
    controlStateLabel: rmText('labels.controlState'),
    actionsLabel: rmText('labels.actions'),
  };
}

export type SkillLibraryRow = {
  id: string;
  name: string;
  displayName: string;
  description: string;
  status: string;
  currentVersion: string;
  publishedVersion: string;
  tags: string[];
  evidenceState: EvidenceState;
  runtimeExposure: string;
  controlState: 'supported' | 'contract-gap';
  detailHref: string;
};

export type SkillContentPanel = {
  title: string;
  source: string;
  body: string;
  scope: string;
  when: string;
};

export type SkillStringRow = {
  id: string;
  value: string;
};

export type SkillKeyValueRow = {
  id: string;
  key: string;
  value: string;
  valueLocked?: boolean;
  rawValue?: unknown;
};

export type SkillStructuredDraftSpec = {
  body: string;
  scope: string;
  agents: SkillStringRow[];
  platforms: SkillStringRow[];
  entry: SkillKeyValueRow[];
  options: SkillKeyValueRow[];
  metadata: SkillKeyValueRow[];
  extra: SkillKeyValueRow[];
};

export type SkillManifestEditorModel = {
  id: string;
  name: string;
  displayName: string;
  description: string;
  status: string;
  currentVersion: number;
  publishedVersion: number;
  tags: SkillStringRow[];
  metadataEditable: boolean;
};

export type SkillEditorModel = {
  body: string;
  scope: string;
  agents: string;
  platforms: string;
  spec: Record<string, unknown>;
  publishedSpec: Record<string, unknown>;
  structured: SkillStructuredDraftSpec;
  manifest: SkillManifestEditorModel;
  baseRevision: number;
  editable: boolean;
  plaintextTrusted: boolean;
  contractGap: string;
};

export type SkillVersionRow = {
  version: number;
  changelog: string;
  createdAt: string;
  createdBy: string;
  validationState: string;
  isCurrent: boolean;
  isPublished: boolean;
};

export type SkillConsumerRow = {
  projectName: string;
  effectiveState: string;
  layers: string;
  runtimeLoaded: boolean;
  configHref: string;
  runtimeHref: string;
};

export type SkillDraftJsonDrawerModel = {
  draftJson: string;
  publishedJson: string;
  diffText: string;
};

export type SkillMutationPreview = {
  kind: string;
  title: string;
  available: boolean;
  confirmationKey: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  body: Record<string, unknown>;
  bodyJson: string;
  evidence: string;
  contractGap: string;
  destructive: boolean;
};

export type SkillLifecycleKind = 'save_draft' | 'validate' | 'preview' | 'create_version' | 'publish' | 'rollback' | 'activate' | 'disable' | 'archive' | 'delete';

export type SkillDraftInput = {
  body?: string;
  scope?: string;
  agents?: string;
  platforms?: string;
  spec?: Record<string, unknown>;
  userEditedBody?: boolean;
  changelog?: string;
  targetVersion?: number;
};

export type SkillCreateInput = {
  name: string;
  displayName?: string;
  description?: string;
};

export type SkillDetailModel = {
  skillId: string;
  name: string;
  displayName: string;
  artifactStatus: string;
  currentVersion: number;
  publishedVersion: number;
  latestVersion: number;
  hasDraft: boolean;
  header: HeaderModel;
  status: StatusItem[];
  summaryRows: Array<[string, string]>;
  contentPanels: SkillContentPanel[];
  editor: SkillEditorModel;
  versions: SkillVersionRow[];
  consumers: SkillConsumerRow[];
  draftJsonDrawer: SkillDraftJsonDrawerModel;
  contractGaps: string[];
  rawJson: string;
  partialNotices: string[];
  readonlyState: string;
};

export type SkillLibraryModel = {
  header: HeaderModel;
  status: StatusItem[];
  skills: SkillLibraryRow[];
  rawJson: string;
  partialNotices: string[];
  emptyState: { title: string; description: string } | null;
};

export type LoadedSkillRow = {
  personaName: string;
  skillName: string;
  displayName: string;
  scope: string;
  sourceOrigin: string;
  managed: boolean;
  enabled: boolean;
  artifactId: string;
  evidenceState: EvidenceState;
  runtimeEvidence: string;
  detailHref: string;
};

export type LoadedSkillsModel = {
  header: HeaderModel;
  status: StatusItem[];
  loadedSkills: LoadedSkillRow[];
  rawJson: string;
  partialNotices: string[];
  emptyState: { title: string; description: string } | null;
};

export type MCPServerRow = {
  id: string;
  name: string;
  displayName: string;
  status: string;
  type: string;
  mode: MCPMode;
  modeLabel: string;
  configModel: string;
  runtimeName: string;
  proxy: boolean;
  credentialPolicy: string;
  evidenceState: EvidenceState;
  runtimeExposure: string;
  controlState: 'contract-gap';
  usedBy: string[];
  usedByLabel: string;
  detailHref: string;
};

export type MCPSecretRefModel = {
  key: string;
  target: string;
  required: boolean;
  visibility: string;
};

export type MCPProxyRuleModel = {
  tool: string;
  descriptionAppend: string;
  permission: string;
  blockedMessage: string;
  audit: string;
  defaultParams: string;
};

export type MCPUsageConsumerRow = {
  key: string;
  personaId: string;
  personaName: string;
  source: string;
  referencedVersion: string;
  runtimeState: string;
  configHref: string;
  runtimeHref: string;
};

export type MCPRuntimeEvidenceRow = {
  key: string;
  personaName: string;
  serverName: string;
  mode: MCPMode;
  activeProxyState: string;
  sourceOrigin: string;
  configPrecedence: string;
  lastCall: string;
  auditHealth: string;
  toolVisibility: string;
  runtimeHref: string;
};

export type MCPDraftJsonDrawerModel = {
  draftJson: string;
  publishedJson: string;
  diffText: string;
};

export type MCPServerDetailModel = {
  header: HeaderModel;
  status: StatusItem[];
  summaryRows: Array<[string, string]>;
  artifactStatus: string;
  mode: MCPMode;
  modeLabel: string;
  editableSpec: MCPDefinitionDraftSpec;
  publishedSpec: MCPDefinitionDraftSpec;
  nativeFields: Array<[string, string]>;
  proxyFields: Array<[string, string]>;
  secretRefs: MCPSecretRefModel[];
  proxyRules: MCPProxyRuleModel[];
  consumers: MCPUsageConsumerRow[];
  runtimeEvidence: MCPRuntimeEvidenceRow[];
  auditEventsCount: number;
  draftJsonDrawer: MCPDraftJsonDrawerModel;
  contractGaps: string[];
  toolDefinitionState: string;
  rawJson: string;
  partialNotices: string[];
  readonlyState: string;
};

export type MCPServersModel = {
  header: HeaderModel;
  status: StatusItem[];
  servers: MCPServerRow[];
  nativeCount: number;
  proxyCount: number;
  rawJson: string;
  partialNotices: string[];
  emptyState: { title: string; description: string } | null;
};

export type MCPDefinitionDraftSpec = {
  type: string;
  command: string;
  args: string[];
  env?: Record<string, string>;
  url: string;
  proxy: boolean;
  credential_visibility: string;
  secret_refs: MCPSecretRefModel[];
  proxy_rules: Array<{
    tool: string;
    description_append?: string;
    blocked?: boolean;
    blocked_message?: string;
    audit?: boolean;
    audit_params?: boolean;
    default_params?: Record<string, unknown>;
  }>;
  runtime_name?: string;
};

export type MCPPresetRow = {
  name: string;
  displayName: string;
  description: string;
  type: string;
  proxy: boolean;
  credentialPolicy: string;
  evidenceState: EvidenceState;
  actionState: string;
  detailHref: string;
};

export type MCPPresetsModel = {
  header: HeaderModel;
  status: StatusItem[];
  presets: MCPPresetRow[];
  rawJson: string;
  partialNotices: string[];
  emptyState: { title: string; description: string } | null;
};

export type ToolProxyRow = {
  personaName: string;
  serverName: string;
  type: string;
  proxy: boolean;
  localProxyAddr: string;
  credentialVisibility: string;
  sourceOrigin: string;
  proxyRulesCount: number;
  artifactId: string;
  // Whether the running agent adapter can be handed this server at all. A
  // running proxy and a local_proxy_addr describe cc-connect, not the agent:
  // an adapter without MCPManagedServerSetter is never given the definition,
  // so without this the page rendered a healthy row for an agent with zero
  // tools. undefined means the snapshot does not answer the question.
  deliveredToAgent: boolean | undefined;
  evidenceState: EvidenceState;
  runtimeEvidence: string;
  detailHref: string;
};

export type ToolProxyModel = {
  header: HeaderModel;
  status: StatusItem[];
  proxyRows: ToolProxyRow[];
  shadowedRows: Record<string, unknown>[];
  rawJson: string;
  partialNotices: string[];
  emptyState: { title: string; description: string } | null;
};

export function buildSkillLibraryModel(sources: SkillLibrarySources, options: ResourceModelOptions = {}): SkillLibraryModel {
  const copy = createResourceManagementCopy(options);
  const skills = getArray(asRecord(sources.skills).skills).map(value => skillLibraryRow(value, copy));
  const conflictCount = conflictCountFrom(sources.conflicts);
  return {
    header: {
      title: 'Skill Library',
      description: 'Live managed skill artifacts from /api/v1/skills. Persona binding remains in Persona config.',
      meta: [['Kind', 'Managed skill artifacts'], ['Source', '/api/v1/skills'], ['Evidence', 'Configured artifact list'] ],
    },
    status: [
      { label: 'Skills', value: String(skills.length), evidence: 'Live artifact list', tone: skills.length ? 'green' : 'muted' },
      { label: 'Runtime exposure', value: copy.runtimeNotInferredStatus, evidence: copy.requiresRuntimeProfileEvidence, tone: 'muted' },
      { label: 'Conflicts', value: String(conflictCount), evidence: '/api/v1/skills/conflicts', tone: conflictCount ? 'yellow' : 'green' },
      { label: 'Source', value: 'Managed definitions', evidence: '/api/v1/skills', tone: 'blue' },
    ],
    skills,
    rawJson: redactedJson(sources, { redactPromptContent: true }),
    partialNotices: partialNotices(sources.errors),
    emptyState: skills.length ? null : { title: 'No managed skills returned.', description: 'The management API returned an empty Skill Library.' },
  };
}

export function buildSkillDetailModel(sources: SkillDetailSources, options: ResourceModelOptions = {}): SkillDetailModel {
  const copy = createResourceManagementCopy(options);
  const skillRecord = asRecord(sources.skill);
  const manifest = asRecord(skillRecord.manifest);
  const fallbackLatestVersion = asRecord(skillRecord.latest_version);
  const draft = asRecord(skillRecord.draft);
  const versions = skillVersionRows(sources.versions, fallbackLatestVersion, manifest);
  const latestVersion = versions.reduce((max, version) => Math.max(max, version.version), numberValue(fallbackLatestVersion.version));
  const publishedVersion = numberValue(manifest.published_version);
  const currentVersion = numberValue(manifest.current_version);
  const draftSpec = parseSpec(draft.spec);
  const previewSpec = skillPreviewSpec(sources.preview);
  const publishedSpec = skillPublishedSpec(sources.versions, fallbackLatestVersion, publishedVersion);
  const editorSpec = Object.keys(previewSpec).length ? previewSpec : Object.keys(draftSpec).length ? draftSpec : publishedSpec;
  const editorDisplaySpec = redactedSkillSpecForDisplay(editorSpec);
  const publishedDisplaySpec = redactedSkillSpecForDisplay(publishedSpec);
  const rawEditorBody = text(editorSpec.body);
  const redactedEditorBody = text(editorDisplaySpec.body);
  const consumers = getArray(asRecord(sources.installations).installations).map(item => skillConsumerRow(item, text(manifest.name), text(manifest.id)));
  const title = text(manifest.display_name) || text(manifest.name) || 'Skill detail';
  const draftJson = redactedJson(editorDisplaySpec);
  const publishedJson = redactedJson(publishedDisplaySpec);
  const contractGaps = [
    'Persona binding/install/unbind is intentionally read-only here; edit bindings in Persona config.',
    'Backend destructive preview/confirmation endpoint gap: PATCH archive and DELETE execute directly; Console shows client-side request preview plus explicit confirmation.',
    'Metadata edit endpoint gap: backend supports create metadata and status changes, but not display name/description updates for existing Skill manifests.',
  ];
  return {
    skillId: text(manifest.id) || text(manifest.name),
    name: text(manifest.name) || 'unknown-skill',
    displayName: title,
    artifactStatus: text(manifest.status) || 'unknown',
    currentVersion,
    publishedVersion,
    latestVersion,
    hasDraft: draft.revision !== undefined,
    header: {
      title,
      description: text(manifest.description) || 'Managed skill artifact detail with Markdown-first content and read-only usage evidence.',
      meta: [['Skill', text(manifest.name) || 'Unknown'], ['Artifact ID', text(manifest.id) || 'Unavailable'], ['Source', '/api/v1/skills/{id}']],
    },
    status: [
      { label: 'Status', value: text(manifest.status) || 'unknown', evidence: 'Artifact manifest', tone: statusTone(text(manifest.status)) },
      { label: 'Published', value: String(publishedVersion), evidence: 'published_version', tone: publishedVersion !== 0 ? 'green' : 'muted' },
      { label: 'Draft revision', value: numberText(draft.revision), evidence: 'draft.revision', tone: draft.revision ? 'blue' : 'muted' },
      { label: 'Runtime exposure', value: consumers.length ? 'Partial' : 'Unknown', evidence: '/installations evidence only; no persona/platform inference', tone: consumers.length ? 'yellow' : 'muted' },
      { label: 'Source', value: 'Managed definition', evidence: '/api/v1/skills/{id}', tone: 'blue' },
    ],
    summaryRows: [
      ['Name', text(manifest.name) || 'Unavailable'],
      ['Status', text(manifest.status) || 'Unavailable'],
      ['Current version', String(currentVersion)],
      ['Published version', String(publishedVersion)],
      ['Tags', getStrings(manifest.tags).join(', ') || 'None'],
      ['Latest version', String(latestVersion)],
      [copy.evidenceStateLabel, copy.configuredRuntimeUnknown],
      ['Usage boundary', 'Persona binding is read-only here; edit bindings in Persona config'],
    ],
    contentPanels: skillContentPanels(publishedSpec, Object.keys(previewSpec).length ? previewSpec : draftSpec, draft.revision),
    editor: {
      body: redactedEditorBody,
      scope: text(editorDisplaySpec.scope),
      agents: getStrings(asRecord(editorDisplaySpec.when).agent).join(', '),
      platforms: getStrings(asRecord(editorDisplaySpec.when).platform).join(', '),
      spec: editorDisplaySpec,
      publishedSpec: publishedDisplaySpec,
      structured: skillStructuredDraftSpecFromSpec(editorDisplaySpec),
      manifest: {
        id: text(manifest.id),
        name: text(manifest.name),
        displayName: text(manifest.display_name),
        description: text(manifest.description),
        status: text(manifest.status),
        currentVersion,
        publishedVersion,
        tags: stringRows('tag', getStrings(manifest.tags)),
        metadataEditable: false,
      },
      baseRevision: numberValue(draft.revision),
      editable: Boolean(text(manifest.id) || text(manifest.name)) && text(manifest.status) !== 'archived',
      plaintextTrusted: redactedEditorBody === rawEditorBody && configValuesEqual(editorDisplaySpec, editorSpec),
      contractGap: text(manifest.status) === 'archived' ? 'Archived Skill artifacts are terminal in the backend contract.' : '',
    },
    versions,
    consumers,
    draftJsonDrawer: {
      draftJson,
      publishedJson,
      diffText: diffJsonLines(publishedJson, draftJson),
    },
    contractGaps,
    rawJson: redactedJson(sources, { redactPromptContent: true }),
    partialNotices: partialNotices(sources.errors),
    readonlyState: 'Managed Skill definition with read-only consumers and runtime evidence navigation.',
  };
}

export function buildLoadedSkillsModel(sources: LoadedSkillsSources, options: ResourceModelOptions = {}): LoadedSkillsModel {
  const copy = createResourceManagementCopy(options);
  const rows = personaRecords(sources.personaDetails).flatMap(loadedSkillRowsForPersona);
  const personaCount = getArray(asRecord(sources.personas).personas).length || personaRecords(sources.personaDetails).length;
  const notices = partialNotices(sources.errors);
  notices.push(copy.loadedSkillsRuntimeOnly);
  notices.push(copy.runtimeAssociationNotice);
  return {
    header: {
      title: 'Loaded Skills',
      description: 'Runtime-backed skill visibility from live persona detail snapshots. This page does not infer exposure from configuration alone.',
      meta: [['Kind', 'Runtime-loaded skills'], ['Source', '/api/v1/personas/{name}'], ['Exposure evidence', 'Live persona detail only']],
    },
    status: [
      { label: 'Personas checked', value: String(personaCount), evidence: '/api/v1/personas', tone: personaCount ? 'blue' : 'muted' },
      { label: 'Runtime-backed', value: String(rows.length), evidence: 'Live persona skill snapshots', tone: rows.length ? 'green' : 'muted' },
      { label: 'Managed', value: String(rows.filter(row => row.managed).length), evidence: 'skill.managed=true', tone: 'blue' },
      { label: 'Partial/unknown', value: String(notices.length), evidence: 'Unavailable data is explicit', tone: notices.length ? 'yellow' : 'green' },
    ],
    loadedSkills: rows,
    rawJson: redactedJson(sources, { redactPromptContent: true }),
    partialNotices: notices,
    emptyState: rows.length ? null : { title: 'No live loaded skills returned.', description: 'Loaded skills appear only when live persona detail endpoints expose skill snapshots.' },
  };
}

export function buildMCPServersModel(sources: MCPSources, options: ResourceModelOptions = {}): MCPServersModel {
  const copy = createResourceManagementCopy(options);
  const servers = getArray(asRecord(sources.mcps).mcps).map(value => {
    const row = mcpServerRow(value, copy);
    const usedBy = mcpUsedBy(mcpMatchCandidates(row.id, row.name, row.runtimeName), sources.personaDetails);
    return {
      ...row,
      usedBy,
      usedByLabel: usedBy.length ? usedBy.join(', ') : 'No live consumers',
    };
  });
  const nativeCount = servers.filter(server => server.mode === 'native').length;
  const proxyCount = servers.filter(server => server.mode === 'proxy').length;
  const conflictCount = conflictCountFrom(sources.conflicts);
  const usedByCount = new Set(servers.flatMap(server => server.usedBy)).size;
  const notices = partialNotices(sources.errors);
  if (sources.personaDetails) {
    notices.push('Used-by evidence is read-only and aggregated from live Persona MCP snapshots; Persona assignment remains in Persona config.');
  }
  return {
    header: {
      title: 'MCP Servers',
      description: 'Managed MCP definitions split into Native MCP connection configs and cc-connect Proxy MCP policy configs.',
      meta: [['Kind', 'Native + cc-connect Proxy MCPs'], ['Source', '/api/v1/mcps'], ['Evidence', 'Configured artifact list']],
    },
    status: [
      { label: 'Definitions', value: String(servers.length), evidence: 'Live artifact list', tone: servers.length ? 'green' : 'muted' },
      { label: 'Native MCPs', value: String(nativeCount), evidence: 'proxy=false or absent', tone: nativeCount ? 'blue' : 'muted' },
      { label: 'Proxy MCPs', value: String(proxyCount), evidence: 'proxy=true', tone: proxyCount ? 'green' : 'muted' },
      { label: 'Runtime exposure', value: copy.runtimeNotInferredStatus, evidence: copy.requiresRuntimeProfileEvidence, tone: 'muted' },
      { label: 'Used by', value: String(usedByCount), evidence: 'Read-only Persona runtime snapshots', tone: usedByCount ? 'green' : 'muted' },
      { label: 'Conflicts', value: String(conflictCount), evidence: '/api/v1/mcps/conflicts', tone: conflictCount ? 'yellow' : 'green' },
      { label: 'Actions', value: 'Drawer gated', evidence: 'Create, import, edit, prompt/policy, and runtime evidence open on demand', tone: 'blue' },
    ],
    servers,
    nativeCount,
    proxyCount,
    rawJson: redactedJson(sources, { redactPromptContent: true }),
    partialNotices: notices,
    emptyState: servers.length ? null : { title: 'No managed MCP definitions returned.', description: 'The management API returned an empty MCP server list.' },
  };
}

export function buildMCPServerDetailModel(sources: MCPDetailSources, options: ResourceModelOptions = {}): MCPServerDetailModel {
  const copy = createResourceManagementCopy(options);
  const mcpRecord = asRecord(sources.mcp);
  const manifest = asRecord(mcpRecord.manifest);
  const spec = draftFirstSpec(mcpRecord);
  const draftSpec = draftSpecForEditing(spec);
  const mode = mcpMode(spec);
  const modeLabel = mcpModeLabel(mode);
  const proxyRules = proxyRuleRows(spec.proxy_rules);
  const secretRefs = secretRefRows(spec.secret_refs);
  const matchCandidates = mcpMatchCandidates(text(manifest.id), text(manifest.name), text(spec.runtime_name));
  const consumers = mcpConsumersForPersonaDetails(matchCandidates, text(manifest.name), sources.personaDetails);
  const runtimeEvidence = mcpRuntimeEvidenceForPersonaDetails(matchCandidates, text(manifest.name), sources.personaDetails, sources.audit);
  const auditEventsCount = getArray(asRecord(sources.audit).events).length;
  const credentialPolicy = text(spec.credential_visibility) || (secretRefs.length ? 'proxy_only' : 'Unspecified');
  const type = text(spec.type) || 'Unknown';
  const draftJson = redactedJson(draftSpec, { redactPromptContent: true });
  const publishedSpec = latestPublishedSpec(mcpRecord);
  const publishedDraftSpec = Object.keys(publishedSpec).length ? draftSpecForEditing(publishedSpec) : draftSpec;
  const publishedJson = redactedJson(publishedDraftSpec, { redactPromptContent: true });
  const contractGaps = mcpContractGaps(mode, copy);
  const artifactStatus = text(manifest.status) || 'unknown';
  return {
    header: {
      title: text(manifest.display_name) || text(manifest.name) || 'MCP server detail',
      description: text(manifest.description) || `${modeLabel} definition detail with credential-bearing fields redacted.`,
      meta: [['MCP', text(manifest.name) || 'Unknown'], ['Mode', modeLabel], ['Artifact ID', text(manifest.id) || 'Unavailable'], ['Source', '/api/v1/mcps/{id}']],
    },
    status: [
      { label: 'Status', value: artifactStatus, evidence: 'Artifact manifest', tone: statusTone(artifactStatus) },
      { label: 'Mode', value: modeLabel, evidence: mode === 'proxy' ? 'proxy=true' : 'proxy=false/absent', tone: mode === 'proxy' ? 'green' : 'blue' },
      { label: 'Transport', value: type, evidence: mode === 'native' ? 'Connection model' : 'Upstream transport', tone: type === 'Unknown' ? 'muted' : 'blue' },
      { label: 'Secret refs', value: String(secretRefs.length), evidence: 'Count only; values redacted', tone: secretRefs.length ? 'yellow' : 'green' },
      { label: 'Runtime exposure', value: 'Unknown', evidence: copy.mcpDefinitionRuntimeUnknown, tone: 'muted' },
      { label: 'Consumers', value: String(consumers.length), evidence: 'Read-only Persona config/runtime evidence', tone: consumers.length ? 'green' : 'muted' },
      { label: 'Proxy evidence', value: String(runtimeEvidence.length), evidence: 'Live persona MCP snapshots', tone: runtimeEvidence.length ? 'blue' : 'muted' },
      { label: 'Controls', value: 'Unavailable', evidence: copy.mutationContractGap, tone: 'yellow' },
    ],
    summaryRows: [
      ['Name', text(manifest.name) || 'Unavailable'],
      ['Status', artifactStatus],
      ['Published version', numberText(manifest.published_version)],
      ['Mode', modeLabel],
      ['Transport', type],
      ['Proxy enabled', boolText(spec.proxy)],
      ['Credential policy', credentialPolicy],
      ['Proxy rules', String(proxyRules.length)],
      ['Runtime name', text(spec.runtime_name) || text(manifest.name) || 'Unavailable'],
      [copy.evidenceStateLabel, copy.configuredRuntimeUnknown],
      [copy.controlStateLabel, copy.contractGap],
    ],
    artifactStatus,
    mode,
    modeLabel,
    editableSpec: draftSpec,
    publishedSpec: publishedDraftSpec,
    nativeFields: nativeConnectionRows(spec),
    proxyFields: proxyPolicyRows(spec),
    secretRefs,
    proxyRules,
    consumers,
    runtimeEvidence,
    auditEventsCount,
    draftJsonDrawer: { draftJson, publishedJson, diffText: diffJsonLines(publishedJson, draftJson) },
    contractGaps,
    toolDefinitionState: 'Tool/schema visibility is partial: authoritative MCP tool schemas are not exposed by current management APIs; proxy rule counts and runtime snapshots are shown as evidence.',
    rawJson: redactedJson(sources, { redactPromptContent: true }),
    partialNotices: partialNotices(sources.errors),
    readonlyState: `${copy.contractGap}: destructive or lifecycle actions require backend preview plus explicit confirmation before this view can enable them.`,
  };
}

export function buildMCPPresetsModel(sources: MCPPresetSources, options: ResourceModelOptions = {}): MCPPresetsModel {
  const copy = createResourceManagementCopy(options);
  const presets = getArray(asRecord(sources.presets).presets).map(value => mcpPresetRow(value, copy));
  return {
    header: {
      title: 'MCP Presets',
      description: copy.presetPlannedDescription,
      meta: [['Kind', 'MCP preset registry'], ['Source', '/api/v1/mcp_presets'], [copy.actionsLabel, copy.presetInstallAvailable]],
    },
    status: [
      { label: 'Presets', value: String(presets.length), evidence: 'Backend registry', tone: presets.length ? 'green' : 'muted' },
      { label: 'Proxy presets', value: String(presets.filter(preset => preset.proxy).length), evidence: 'preset.proxy', tone: 'blue' },
      { label: 'Credential values', value: 'Redacted', evidence: 'Required bindings only', tone: 'green' },
      { label: 'Install', value: 'Available', evidence: copy.presetInstallAvailable, tone: 'green' },
    ],
    presets,
    rawJson: redactedJson(sources, { redactPromptContent: true }),
    partialNotices: partialNotices(sources.errors),
    emptyState: presets.length ? null : { title: 'No MCP presets returned.', description: 'The backend registry returned an empty preset list.' },
  };
}

export function buildToolProxyModel(sources: ToolProxySources, options: ResourceModelOptions = {}): ToolProxyModel {
  const copy = createResourceManagementCopy(options);
  const personaDetails = personaRecords(sources.personaDetails);
  const proxyRows = personaDetails.flatMap(toolProxyRowsForPersona);
  const shadowedRows = personaDetails.flatMap(shadowedRowsForPersona);
  const personaCount = getArray(asRecord(sources.personas).personas).length || personaDetails.length;
  const notices = partialNotices(sources.errors);
  notices.push('Tool definitions are partial: current management APIs expose proxy servers and policy rule counts, not authoritative runtime tool schemas.');
  return {
    header: {
      title: 'Tool Proxy',
      description: 'Runtime-backed MCP proxy visibility from live persona details. Credential values and tool schemas are not rendered.',
      meta: [['Kind', 'MCP proxy runtime evidence'], ['Source', '/api/v1/personas/{name}.mcp'], ['Tool schemas', 'Partial/read-only']],
    },
    status: [
      { label: 'Personas checked', value: String(personaCount), evidence: '/api/v1/personas', tone: personaCount ? 'blue' : 'muted' },
      { label: 'Proxy rows', value: String(proxyRows.length), evidence: 'Live MCP snapshots', tone: proxyRows.length ? 'green' : 'muted' },
      { label: 'Local proxies', value: String(proxyRows.filter(row => row.localProxyAddr).length), evidence: 'local_proxy_addr', tone: 'blue' },
      { label: 'Shadowed', value: String(shadowedRows.length), evidence: 'shadowed_managed', tone: shadowedRows.length ? 'yellow' : 'green' },
    ],
    proxyRows,
    shadowedRows,
    rawJson: redactedJson(sources, { redactPromptContent: true }),
    partialNotices: notices,
    emptyState: proxyRows.length ? null : { title: 'No live MCP proxy rows returned.', description: 'Tool proxy rows appear only when persona detail endpoints expose MCP runtime snapshots.' },
  };
}

function redactedSkillSpecForDisplay(spec: Record<string, unknown>): Record<string, unknown> {
  return asRecord(redactForDisplay(spec));
}

export function skillStructuredDraftSpecFromSpec(spec: Record<string, unknown>): SkillStructuredDraftSpec {
  const when = asRecord(spec.when);
  return {
    body: text(spec.body),
    scope: text(spec.scope) || 'project',
    agents: stringRows('agent', getStrings(when.agent)),
    platforms: stringRows('platform', getStrings(when.platform)),
    entry: keyValueRows('entry', asRecord(spec.entry)),
    options: keyValueRows('option', asRecord(spec.options)),
    metadata: keyValueRows('metadata', asRecord(spec.metadata)),
    extra: keyValueRows('extra', Object.fromEntries(Object.entries(spec).filter(([key]) => !['body', 'scope', 'when', 'entry', 'options', 'metadata'].includes(key)))),
  };
}

export function skillSpecFromStructuredDraft(draft: SkillStructuredDraftSpec): Record<string, unknown> {
  const spec: Record<string, unknown> = { body: draft.body };
  if (draft.scope.trim()) spec.scope = draft.scope.trim();
  const agents = stringRowsToValues(draft.agents);
  const platforms = stringRowsToValues(draft.platforms);
  if (agents.length || platforms.length) {
    spec.when = {
      ...(agents.length ? { agent: agents } : {}),
      ...(platforms.length ? { platform: platforms } : {}),
    };
  }
  const entry = keyValueRowsToRecord(draft.entry);
  const options = keyValueRowsToRecord(draft.options);
  const metadata = keyValueRowsToRecord(draft.metadata);
  const extra = keyValueRowsToRecord(draft.extra);
  if (Object.keys(entry).length) spec.entry = entry;
  if (Object.keys(options).length) spec.options = options;
  if (Object.keys(metadata).length) spec.metadata = metadata;
  return { ...extra, ...spec };
}

function stringRows(prefix: string, values: string[]): SkillStringRow[] {
  return values.map((value, index) => ({ id: `${prefix}-${index}`, value }));
}

function stringRowsToValues(rows: SkillStringRow[]) {
  return Array.from(new Set(rows.map(row => row.value.trim()).filter(Boolean)));
}

function keyValueRows(prefix: string, record: Record<string, unknown>): SkillKeyValueRow[] {
  return Object.entries(record).map(([key, value], index) => {
    const displayValue = valueForKeyValueRow(key, value);
    return {
      id: `${prefix}-${index}`,
      key,
      value: displayValue,
      valueLocked: displayValue === REDACTED_VALUE && isSensitiveKey(key),
      rawValue: value,
    };
  });
}

function keyValueRowsToRecord(rows: SkillKeyValueRow[]) {
  const record: Record<string, unknown> = {};
  rows.forEach(row => {
    const key = row.key.trim();
    if (!key) return;
    record[key] = parseKeyValueRowValue(row.value);
  });
  return record;
}

function valueForKeyValueRow(key: string, value: unknown) {
  if (isSensitiveKey(key) && value !== undefined && value !== null && value !== '') return REDACTED_VALUE;
  if (typeof value === 'string') return redactSensitiveText(value);
  if (value === undefined || value === null) return '';
  return JSON.stringify(redactForDisplay(value, key), null, 2);
}

function parseKeyValueRowValue(value: string): unknown {
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function containsRedactedValue(value: unknown): boolean {
  if (value === REDACTED_VALUE) return true;
  if (typeof value === 'string') return value.includes(REDACTED_VALUE);
  if (Array.isArray(value)) return value.some(containsRedactedValue);
  if (value && typeof value === 'object') return Object.values(value as Record<string, unknown>).some(containsRedactedValue);
  return false;
}

function configValuesEqual(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function skillLibraryRow(value: unknown, copy: ResourceManagementCopy): SkillLibraryRow {
  const record = asRecord(value);
  return {
    id: text(record.id) || text(record.name),
    name: text(record.name) || text(record.id) || 'unknown-skill',
    displayName: text(record.display_name) || text(record.name) || text(record.id) || 'Unknown skill',
    description: text(record.description),
    status: text(record.status) || 'unknown',
    currentVersion: numberText(record.current_version),
    publishedVersion: numberText(record.published_version),
    tags: getStrings(record.tags),
    evidenceState: 'configured',
    runtimeExposure: copy.runtimeNotInferred,
    controlState: 'supported',
    detailHref: `#/skills/library/${encodeURIComponent(text(record.id) || text(record.name))}`,
  };
}

export function buildSkillLifecyclePreview(model: SkillDetailModel, kind: SkillLifecycleKind, draftInput: SkillDraftInput = {}): SkillMutationPreview {
  const skillId = model.skillId || model.name;
  const terminalGap = model.artifactStatus === 'archived' ? 'Archived Skill artifacts are terminal in the backend contract.' : '';
  if (kind === 'save_draft') {
    const spec = draftInput.spec ?? skillSpecForJson(draftInput.body ?? model.editor.body, draftInput.scope ?? model.editor.scope, draftInput.agents ?? model.editor.agents, draftInput.platforms ?? model.editor.platforms);
    const body = text(spec.body);
    const userEditedBody = draftInput.userEditedBody === true || (draftInput.body !== undefined && draftInput.body !== model.editor.body);
    const sourceTrusted = userEditedBody || model.editor.plaintextTrusted;
    const redactedPlaceholderPresent = containsRedactedValue(spec);
    const available = model.editor.editable && body.trim().length > 0 && sourceTrusted && !redactedPlaceholderPresent;
    return skillMutationPreview({
      kind,
      title: 'Save draft',
      available,
      method: 'PUT',
      endpoint: `/skills/${encodeURIComponent(skillId)}/draft`,
      body: {
        spec,
        base_revision: model.editor.baseRevision,
        actor: 'console',
      },
      evidence: available ? 'POST-free draft save with optimistic base_revision; structured fields produce the Skill JSON draft and raw JSON stays in the drawer.' : model.editor.contractGap || 'Draft body must be non-empty and must not include redacted text placeholders.',
      contractGap: available ? '' : model.editor.contractGap || 'Draft body must be non-empty and must not include redacted text placeholders.',
      destructive: false,
    });
  }
  if (kind === 'validate') {
    const available = model.hasDraft && !terminalGap;
    return skillMutationPreview({
      kind,
      title: 'Validate draft',
      available,
      method: 'POST',
      endpoint: `/skills/${encodeURIComponent(skillId)}/validate`,
      body: {},
      evidence: available ? 'Backend supports read-only draft validation via POST /skills/{id}/validate.' : terminalGap || 'No draft revision returned by /skills/{id}.',
      contractGap: available ? '' : terminalGap || 'No draft revision returned by /skills/{id}.',
      destructive: false,
    });
  }
  if (kind === 'preview') {
    const available = model.hasDraft && !terminalGap;
    return skillMutationPreview({
      kind,
      title: 'Preview draft',
      available,
      method: 'GET',
      endpoint: `/skills/${encodeURIComponent(skillId)}/preview`,
      body: {},
      evidence: available ? 'Backend supports structured draft preview via GET /skills/{id}/preview.' : terminalGap || 'No draft revision returned by /skills/{id}.',
      contractGap: available ? '' : terminalGap || 'No draft revision returned by /skills/{id}.',
      destructive: false,
    });
  }
  if (kind === 'create_version') {
    const available = model.hasDraft && !terminalGap;
    return skillMutationPreview({
      kind,
      title: 'Create version',
      available,
      method: 'POST',
      endpoint: `/skills/${encodeURIComponent(skillId)}/versions`,
      body: { changelog: draftInput.changelog || 'Console v2 guarded Skill version', actor: 'console' },
      evidence: available ? 'Backend creates immutable versions from the current validated draft.' : terminalGap || 'No draft revision returned by /skills/{id}.',
      contractGap: available ? '' : terminalGap || 'No draft revision returned by /skills/{id}.',
      destructive: false,
    });
  }
  if (kind === 'publish') {
    const version = draftInput.targetVersion || model.latestVersion;
    const available = version > 0 && version !== model.publishedVersion && !terminalGap;
    return skillMutationPreview({
      kind,
      title: 'Publish version',
      available,
      method: 'POST',
      endpoint: `/skills/${encodeURIComponent(skillId)}/publish`,
      body: { version, actor: 'console' },
      evidence: available ? `Publishes Skill artifact version v${version}; Persona consumers remain unchanged.` : terminalGap || 'No unpublished version is available to publish.',
      contractGap: available ? '' : terminalGap || 'No unpublished version is available to publish.',
      destructive: false,
    });
  }
  if (kind === 'rollback') {
    const version = draftInput.targetVersion || previousPublishedVersion(model);
    const available = model.publishedVersion > 0 && version > 0 && version < model.publishedVersion && !terminalGap;
    return skillMutationPreview({
      kind,
      title: 'Rollback published pointer',
      available,
      method: 'POST',
      endpoint: `/skills/${encodeURIComponent(skillId)}/rollback`,
      body: { version, actor: 'console' },
      evidence: available ? `Rolls the published pointer back to v${version}; this does not unbind any Persona.` : terminalGap || 'No older version is available for rollback.',
      contractGap: available ? '' : terminalGap || 'No older version is available for rollback.',
      destructive: false,
    });
  }
  if (kind === 'activate' || kind === 'disable' || kind === 'archive') {
    const status = kind === 'activate' ? 'active' : kind === 'disable' ? 'disabled' : 'archived';
    const available = canTransitionSkillStatus(model.artifactStatus, status);
    return skillMutationPreview({
      kind,
      title: kind === 'archive' ? 'Archive skill' : kind === 'activate' ? 'Activate skill' : 'Disable skill',
      available,
      method: 'PATCH',
      endpoint: `/skills/${encodeURIComponent(skillId)}`,
      body: { status, actor: 'console' },
      evidence: available
        ? kind === 'archive'
          ? 'Destructive status transition: backend archives the Skill after this request; review consumers first.'
          : `Backend supports ${model.artifactStatus} → ${status} status transition.`
        : terminalGap || `Backend does not support ${model.artifactStatus} → ${status} transition.`,
      contractGap: available ? '' : terminalGap || `Backend does not support ${model.artifactStatus} → ${status} transition.`,
      destructive: kind === 'archive',
    });
  }
  return skillMutationPreview({
    kind,
    title: 'Delete skill',
    available: model.artifactStatus !== 'archived' && Boolean(skillId),
    method: 'DELETE',
    endpoint: `/skills/${encodeURIComponent(skillId)}?actor=console`,
    body: { actor: 'console' },
    evidence: 'Destructive action: backend DELETE archives the Skill and removes it from the name index; review consumers first.',
    contractGap: model.artifactStatus === 'archived' ? terminalGap : '',
    destructive: true,
  });
}

export function buildSkillCreatePreview(input: SkillCreateInput): SkillMutationPreview {
  const name = input.name.trim();
  const body = {
    name,
    display_name: (input.displayName ?? '').trim(),
    description: (input.description ?? '').trim(),
    actor: 'console',
  };
  const available = name.length > 0;
  return skillMutationPreview({
    kind: 'create',
    title: 'Create Skill artifact',
    available,
    method: 'POST',
    endpoint: '/skills',
    body,
    evidence: available ? 'Creates a managed Skill artifact only; Persona binding remains in Persona config.' : 'Skill name is required before POST /skills can be preview-confirmed.',
    contractGap: available ? '' : 'Skill name is required before POST /skills can be preview-confirmed.',
    destructive: false,
  });
}

function loadedSkillRowsForPersona(personaDetail: Record<string, unknown>): LoadedSkillRow[] {
  const personaName = text(personaDetail.name) || 'unknown-persona';
  const skillsRecord = asRecord(personaDetail.skills);
  return getArray(skillsRecord.skills).map(value => {
    const skill = asRecord(value);
    const skillName = text(skill.name) || 'unknown-skill';
    return {
      personaName,
      skillName,
      displayName: text(skill.display_name) || skillName,
      scope: text(skill.scope) || 'unknown',
      sourceOrigin: text(skill.source_origin) || (skill.managed ? 'managed' : 'native'),
      managed: Boolean(skill.managed),
      enabled: skill.enabled !== false,
      artifactId: text(skill.artifact_id),
      evidenceState: 'runtime-active',
      runtimeEvidence: `Live /api/v1/personas/${personaName} skill snapshot`,
      detailHref: `#/skills/loaded/${encodeURIComponent(personaName)}/${encodeURIComponent(skillName)}`,
    };
  });
}

function skillMutationPreview({
  kind,
  title,
  available,
  method,
  endpoint,
  body,
  evidence,
  contractGap,
  destructive,
}: Omit<SkillMutationPreview, 'confirmationKey' | 'bodyJson'>): SkillMutationPreview {
  const bodyJson = method === 'GET' ? '{}' : redactedJson(body, { redactPromptContent: true });
  return {
    kind,
    title,
    available,
    confirmationKey: `${method} ${endpoint}\n${bodyJson}`,
    method,
    endpoint,
    body,
    bodyJson,
    evidence,
    contractGap,
    destructive,
  };
}

function skillContentPanels(publishedSpec: Record<string, unknown>, draftSpec: Record<string, unknown>, draftRevision: unknown): SkillContentPanel[] {
  const panels: SkillContentPanel[] = [];
  if (Object.keys(draftSpec).length) {
    panels.push(skillContentPanel('Draft / preview content', `draft.revision ${numberText(draftRevision)}`, draftSpec));
  }
  if (Object.keys(publishedSpec).length) {
    panels.push(skillContentPanel('Published content', 'published version', publishedSpec));
  }
  if (!panels.length) {
    panels.push({ title: 'No content available', source: 'No draft or published spec returned', body: '', scope: 'Unavailable', when: 'Unavailable' });
  }
  return panels;
}

function skillContentPanel(title: string, source: string, spec: Record<string, unknown>): SkillContentPanel {
  const when = asRecord(spec.when);
  return {
    title,
    source,
    body: redactSensitiveText(text(spec.body)),
    scope: text(spec.scope) || 'default',
    when: skillWhenText(when),
  };
}

function skillVersionRows(value: unknown, fallbackLatestVersion: Record<string, unknown>, manifest: Record<string, unknown>): SkillVersionRow[] {
  const published = numberValue(manifest.published_version);
  const current = numberValue(manifest.current_version);
  const rows = getArray(asRecord(value).versions).map(version => {
    const record = asRecord(version);
    const validation = asRecord(record.validation_result);
    return {
      version: numberValue(record.version),
      changelog: text(record.changelog) || 'None',
      createdAt: text(record.created_at),
      createdBy: text(record.created_by) || 'unknown',
      validationState: validation.valid === true ? 'valid' : validation.valid === false ? 'invalid' : 'unknown',
      isCurrent: numberValue(record.version) === current,
      isPublished: numberValue(record.version) === published,
    };
  }).filter(row => row.version > 0);
  if (!rows.length && numberValue(fallbackLatestVersion.version) > 0) {
    const validation = asRecord(fallbackLatestVersion.validation_result);
    rows.push({
      version: numberValue(fallbackLatestVersion.version),
      changelog: text(fallbackLatestVersion.changelog) || 'None',
      createdAt: text(fallbackLatestVersion.created_at),
      createdBy: text(fallbackLatestVersion.created_by) || 'unknown',
      validationState: validation.valid === true ? 'valid' : validation.valid === false ? 'invalid' : 'unknown',
      isCurrent: numberValue(fallbackLatestVersion.version) === current,
      isPublished: numberValue(fallbackLatestVersion.version) === published,
    });
  }
  return rows.sort((a, b) => b.version - a.version);
}

function skillConsumerRow(value: unknown, skillName: string, skillId: string): SkillConsumerRow {
  const record = asRecord(value);
  const projectId = text(record.project_id) || text(record.project_name);
  const projectName = text(record.project_name) || projectId || 'unknown-persona';
  const layers = asRecord(record.layers);
  return {
    projectName,
    effectiveState: text(record.effective_state) || 'unknown',
    layers: skillLayersSummary(layers),
    runtimeLoaded: asRecord(layers.materialized).present === true,
    configHref: projectName ? `#/personas/${encodeURIComponent(projectName)}/workbench?tab=config` : '#/personas',
    runtimeHref: skillName ? `#/skills/loaded/${encodeURIComponent(projectName)}/${encodeURIComponent(skillName)}` : `#/skills/loaded/${encodeURIComponent(projectName)}/${encodeURIComponent(skillId)}`,
  };
}

function skillPreviewSpec(value: unknown): Record<string, unknown> {
  const preview = asRecord(value);
  if (!Object.keys(preview).length) return {};
  if (preview.raw !== undefined) return parseSpec(preview.raw);
  const responseMetadataKeys = new Set(['revision', 'validation', 'validation_result', 'valid', 'warnings', 'errors']);
  return Object.fromEntries(Object.entries(preview).filter(([key]) => !responseMetadataKeys.has(key)));
}

function skillPublishedSpec(versionsValue: unknown, fallbackLatestVersion: Record<string, unknown>, publishedVersion: number): Record<string, unknown> {
  if (!publishedVersion) return {};
  const versions = getArray(asRecord(versionsValue).versions).map(version => asRecord(version));
  const published = versions.find(version => numberValue(version.version) === publishedVersion);
  if (published) return parseSpec(published.spec);
  if (numberValue(fallbackLatestVersion.version) === publishedVersion) return parseSpec(fallbackLatestVersion.spec);
  return {};
}

function skillSpecForJson(body: string, scope: string, agents: string, platforms: string): Record<string, unknown> {
  const spec: Record<string, unknown> = { body };
  if (scope.trim()) spec.scope = scope.trim();
  const agentList = splitCsv(agents);
  const platformList = splitCsv(platforms);
  if (agentList.length || platformList.length) {
    spec.when = {
      ...(agentList.length ? { agent: agentList } : {}),
      ...(platformList.length ? { platform: platformList } : {}),
    };
  }
  return spec;
}

function skillWhenText(when: Record<string, unknown>) {
  const agents = getStrings(when.agent);
  const platforms = getStrings(when.platform);
  const parts = [];
  if (agents.length) parts.push(`agents: ${agents.join(', ')}`);
  if (platforms.length) parts.push(`platforms: ${platforms.join(', ')}`);
  return parts.join(' · ') || 'Always';
}

function skillLayersSummary(layers: Record<string, unknown>) {
  const labels: Array<[string, string]> = [
    ['draft', 'draft_declared'],
    ['published', 'published_declared'],
    ['runtime', 'runtime_resolved'],
    ['loaded', 'materialized'],
  ];
  return labels.map(([label, key]) => {
    const layer = asRecord(layers[key]);
    if (layer.present !== true) return `${label}: absent`;
    const version = numberValue(layer.version);
    const enabled = layer.enabled === false ? 'disabled' : 'enabled';
    return `${label}: v${version || '?'} ${enabled}`;
  }).join(' · ');
}

function previousPublishedVersion(model: SkillDetailModel) {
  return model.versions
    .filter(version => version.version > 0 && version.version < model.publishedVersion)
    .sort((a, b) => b.version - a.version)[0]?.version ?? 0;
}

function canTransitionSkillStatus(current: string, target: string) {
  if (current === 'draft') return target === 'active' || target === 'archived';
  if (current === 'active') return target === 'disabled' || target === 'archived';
  if (current === 'disabled') return target === 'active' || target === 'archived';
  return false;
}

function diffJsonLines(before: string, after: string) {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const max = Math.max(beforeLines.length, afterLines.length);
  const lines: string[] = [];
  for (let index = 0; index < max; index += 1) {
    const beforeLine = beforeLines[index];
    const afterLine = afterLines[index];
    if (beforeLine === afterLine) {
      if (beforeLine !== undefined) lines.push(` ${beforeLine}`);
      continue;
    }
    if (beforeLine !== undefined) lines.push(`-${beforeLine}`);
    if (afterLine !== undefined) lines.push(`+${afterLine}`);
  }
  return lines.join('\n');
}

function splitCsv(value: string) {
  return value.split(',').map(item => item.trim()).filter(Boolean);
}

function mcpServerRow(value: unknown, copy: ResourceManagementCopy): MCPServerRow {
  const record = asRecord(value);
  const spec = firstSpec({ latest_version: record.latest_version, draft: record.draft });
  const type = text(spec.type) || text(record.type) || 'Unknown';
  const secretRefsCount = getArray(spec.secret_refs).length || numberValue(record.secret_refs_count);
  const mode = mcpMode(spec, record);
  const proxy = spec.proxy === true || (spec.proxy !== false && record.proxy === true);
  return {
    id: text(record.id) || text(record.name),
    name: text(record.name) || text(record.id) || 'unknown-mcp',
    displayName: text(record.display_name) || text(record.name) || text(record.id) || 'Unknown MCP',
    status: text(record.status) || 'unknown',
    type,
    mode,
    modeLabel: mcpModeLabel(mode),
    configModel: mode === 'proxy' ? 'Proxy prompt/policy/audit config' : 'Connection/transport config',
    runtimeName: text(spec.runtime_name) || text(record.runtime_name),
    proxy,
    credentialPolicy: text(spec.credential_visibility) || text(record.credential_visibility) || (secretRefsCount ? 'proxy_only' : 'Unspecified'),
    evidenceState: 'configured',
    runtimeExposure: copy.runtimeNotInferred,
    controlState: 'contract-gap',
    usedBy: [],
    usedByLabel: 'No live consumers',
    detailHref: `#/tools/mcp-servers/${encodeURIComponent(text(record.id) || text(record.name))}`,
  };
}

function mcpUsedBy(matchCandidates: string[], details: unknown[] | undefined) {
  const users = new Set<string>();
  for (const personaDetail of personaRecords(details)) {
    const personaName = text(personaDetail.name) || 'unknown-persona';
    for (const server of getArray(asRecord(personaDetail.mcp).servers).map(item => asRecord(item))) {
      if (mcpRuntimeRowMatches(server, matchCandidates)) users.add(personaName);
    }
    for (const shadowed of getArray(asRecord(personaDetail.mcp).shadowed_managed).map(item => asRecord(item))) {
      if (mcpRuntimeRowMatches(shadowed, matchCandidates)) users.add(`${personaName} (shadowed)`);
    }
  }
  return Array.from(users).sort((left, right) => left.localeCompare(right));
}

function mcpPresetRow(value: unknown, copy: ResourceManagementCopy): MCPPresetRow {
  const record = asRecord(value);
  const requiredSecrets = getArray(record.required_secrets);
  const requiredCount = requiredSecrets.filter(secret => asRecord(secret).required !== false).length;
  const bindingLabel = requiredCount === 1 ? '1 required binding' : `${requiredCount} required bindings`;
  const name = text(record.name) || 'unknown-preset';
  return {
    name,
    displayName: text(record.display_name) || name,
    description: text(record.description),
    type: text(record.type) || 'Unknown',
    proxy: Boolean(record.proxy),
    credentialPolicy: `${bindingLabel}; values never displayed`,
    evidenceState: 'planned',
    actionState: copy.presetInstallAction,
    detailHref: `#/tools/mcp-presets/${encodeURIComponent(name)}`,
  };
}

function toolProxyRowsForPersona(personaDetail: Record<string, unknown>): ToolProxyRow[] {
  const personaName = text(personaDetail.name) || 'unknown-persona';
  const mcp = asRecord(personaDetail.mcp);
  return getArray(mcp.servers).map(value => {
    const server = asRecord(value);
    const serverName = text(server.name) || 'unknown-server';
    return {
      personaName,
      serverName,
      type: text(server.type) || 'Unknown',
      proxy: Boolean(server.proxy),
      localProxyAddr: text(server.local_proxy_addr),
      credentialVisibility: text(server.credential_visibility) || 'Unspecified',
      sourceOrigin: text(server.source_origin) || 'unknown',
      proxyRulesCount: numberValue(server.proxy_rules_count),
      artifactId: text(server.artifact_id),
      deliveredToAgent: boolValue(server.delivered_to_agent),
      evidenceState: mcpDeliveryEvidenceState(server.delivered_to_agent),
      runtimeEvidence: mcpDeliveryEvidence(personaName, server.delivered_to_agent),
      detailHref: `#/tools/tool-proxy/${encodeURIComponent(personaName)}/${encodeURIComponent(serverName)}`,
    };
  });
}

// mcpDeliveryEvidenceState answers "is this server live for the agent", which
// is not the same question as "is this server configured and proxied here".
//
// The three cases are deliberately distinct. false is the observation trap the
// backing field exists to break: the proxy is running and logged, the snapshot
// carries a local_proxy_addr, and the agent still has no such tool. undefined
// is an older snapshot that cannot answer, and reporting that as runtime-active
// would be the same false claim in a quieter voice.
function mcpDeliveryEvidenceState(delivered: unknown): EvidenceState {
  if (delivered === false) return 'configured';
  if (delivered === true) return 'runtime-active';
  return 'unknown';
}

function mcpDeliveryEvidence(personaName: string, delivered: unknown): string {
  if (delivered === false) {
    return `Declared, but the ${personaName} agent adapter cannot receive it: the agent has no such tool`;
  }
  if (delivered === true) return `Live /api/v1/personas/${personaName} MCP snapshot`;
  return `/api/v1/personas/${personaName} MCP snapshot does not report delivery to the agent`;
}

// mcpUndeliveredState is the one string the runtime-evidence tables show in
// place of a proxy address or a "visible in runtime snapshot" claim.
const mcpUndeliveredState = 'declared; the agent adapter cannot receive it';

function shadowedRowsForPersona(personaDetail: Record<string, unknown>): Record<string, unknown>[] {
  const personaName = text(personaDetail.name) || 'unknown-persona';
  return getArray(asRecord(personaDetail.mcp).shadowed_managed).map(value => ({ persona_name: personaName, ...asRecord(value) }));
}

function mcpMode(spec: Record<string, unknown>, fallback?: Record<string, unknown>): MCPMode {
  if (spec.proxy === true) return 'proxy';
  if (spec.proxy === false) return 'native';
  return fallback?.proxy === true ? 'proxy' : 'native';
}

function mcpModeLabel(mode: MCPMode) {
  return mode === 'proxy' ? 'cc-connect Proxy MCP' : 'Native MCP';
}

function latestPublishedSpec(record: Record<string, unknown>): Record<string, unknown> {
  return parseSpec(asRecord(record.latest_version).spec);
}

function draftSpecForEditing(spec: Record<string, unknown>): MCPDefinitionDraftSpec {
  const secretRefs = secretRefRows(spec.secret_refs);
  const proxyRules = getArray(spec.proxy_rules).map(value => {
    const rule = asRecord(value);
    const next: MCPDefinitionDraftSpec['proxy_rules'][number] = {
      tool: text(rule.tool) || '*',
    };
    if (text(rule.description_append)) next.description_append = text(rule.description_append);
    if (rule.blocked === true) next.blocked = true;
    if (text(rule.blocked_message)) next.blocked_message = text(rule.blocked_message);
    if (rule.audit === true) next.audit = true;
    if (rule.audit_params === true) next.audit_params = true;
    if (Object.keys(asRecord(rule.default_params)).length) next.default_params = asRecord(rule.default_params);
    return next;
  });
  const next: MCPDefinitionDraftSpec = {
    type: text(spec.type) || 'stdio',
    command: text(spec.command),
    args: getStrings(spec.args),
    url: text(spec.url),
    proxy: spec.proxy === true,
    credential_visibility: text(spec.credential_visibility) || (secretRefs.length ? 'proxy_only' : 'compat'),
    secret_refs: secretRefs,
    proxy_rules: proxyRules,
  };
  const env = safeMCPEnvRecord(spec.env);
  if (Object.keys(env).length) next.env = env;
  if (text(spec.runtime_name)) next.runtime_name = text(spec.runtime_name);
  return next;
}

function nativeConnectionRows(spec: Record<string, unknown>): Array<[string, string]> {
  return [
    ['Transport', text(spec.type) || 'Unset'],
    ['Command', text(spec.command) || 'Not required for HTTP/SSE'],
    ['Args', getStrings(spec.args).join(' ') || 'None'],
    ['Env', Object.keys(asRecord(spec.env)).length ? `${Object.keys(asRecord(spec.env)).length} structured env fields; sensitive values redacted` : 'None'],
    ['URL', text(spec.url) || 'Not required for stdio'],
    ['Secret refs', String(getArray(spec.secret_refs).length)],
    ['Raw headers/env', 'Headers are never displayed; sensitive env placeholders are redacted and credentials use secret_refs'],
  ];
}

function proxyPolicyRows(spec: Record<string, unknown>): Array<[string, string]> {
  const rules = proxyRuleRows(spec.proxy_rules);
  const promptRules = rules.filter(rule => rule.descriptionAppend !== 'None').length;
  const blockedRules = rules.filter(rule => rule.permission !== 'allow').length;
  const auditRules = rules.filter(rule => rule.audit !== 'off').length;
  return [
    ['Proxy prompt injection', promptRules ? `${promptRules} description_append rules` : 'No description_append rules configured'],
    ['Permission policy', blockedRules ? `${blockedRules} blocked/guarded rules` : 'Default allow'],
    ['Audit policy', auditRules ? `${auditRules} audited rules` : 'No audited proxy rules'],
    ['Credential visibility', text(spec.credential_visibility) || 'compat'],
    ['Tool/schema visibility', 'Partial: proxy rules visible; authoritative runtime schemas unavailable'],
  ];
}

function safeMCPEnvRecord(value: unknown): Record<string, string> {
  return Object.fromEntries(
    Object.entries(asRecord(value))
      .map(([key, rawValue]) => {
        const trimmedKey = key.trim();
        if (!trimmedKey) return null;
        const safeValue = isSensitiveKey(trimmedKey) ? REDACTED_VALUE : redactSensitiveText(text(rawValue));
        return [trimmedKey, safeValue] as const;
      })
      .filter((entry): entry is readonly [string, string] => entry !== null),
  );
}

function secretRefRows(value: unknown): MCPSecretRefModel[] {
  return getArray(value).map(item => {
    const ref = asRecord(item);
    return {
      key: text(ref.key) || 'unknown-ref',
      target: text(ref.target) || 'unknown-target',
      required: ref.required !== false,
      visibility: text(ref.visibility) || 'proxy_only',
    };
  });
}

function proxyRuleRows(value: unknown): MCPProxyRuleModel[] {
  return getArray(value).map(item => {
    const rule = asRecord(item);
    return {
      tool: text(rule.tool) || '*',
      descriptionAppend: text(rule.description_append) || 'None',
      permission: rule.blocked === true ? 'blocked' : 'allow',
      blockedMessage: text(rule.blocked_message) || 'Default proxy block message',
      audit: rule.audit === true ? (rule.audit_params === true ? 'audit + params' : 'audit metadata') : 'off',
      defaultParams: Object.keys(asRecord(rule.default_params)).length ? redactedJson(rule.default_params, { redactPromptContent: true }) : 'None',
    };
  });
}

function mcpConsumersForPersonaDetails(matchCandidates: string[], mcpName: string, details: unknown[] | undefined): MCPUsageConsumerRow[] {
  const rows: MCPUsageConsumerRow[] = [];
  for (const personaDetail of personaRecords(details)) {
    const personaName = text(personaDetail.name) || 'unknown-persona';
    for (const server of getArray(asRecord(personaDetail.mcp).servers).map(item => asRecord(item))) {
      if (!mcpRuntimeRowMatches(server, matchCandidates)) continue;
      const serverName = text(server.name) || mcpName || matchCandidates[0] || 'unknown-mcp';
      const artifactID = text(server.artifact_id);
      rows.push({
        key: `${personaName}:${serverName}:consumer`,
        personaId: text(asRecord(personaDetail.control_plane).persona_config_id) || personaName,
        personaName,
        source: text(server.source_origin) || 'runtime snapshot',
        referencedVersion: artifactID && matchCandidates.includes(artifactID) ? 'published ref (version not exposed here)' : 'runtime name match (version not exposed here)',
        runtimeState: server.delivered_to_agent === false
          ? mcpUndeliveredState
          : server.proxy === true ? 'proxy visible in runtime snapshot' : 'native visible in runtime snapshot',
        configHref: `#/personas/${encodeURIComponent(personaName)}/workbench?tab=configuration`,
        runtimeHref: `#/tools/tool-proxy/${encodeURIComponent(personaName)}/${encodeURIComponent(serverName)}`,
      });
    }
  }
  return rows;
}

function mcpRuntimeEvidenceForPersonaDetails(matchCandidates: string[], mcpName: string, details: unknown[] | undefined, audit: unknown): MCPRuntimeEvidenceRow[] {
  const rows: MCPRuntimeEvidenceRow[] = [];
  const auditEventsCount = getArray(asRecord(audit).events).length;
  for (const personaDetail of personaRecords(details)) {
    const personaName = text(personaDetail.name) || 'unknown-persona';
    for (const server of getArray(asRecord(personaDetail.mcp).servers).map(item => asRecord(item))) {
      if (!mcpRuntimeRowMatches(server, matchCandidates)) continue;
      const serverName = text(server.name) || mcpName || matchCandidates[0] || 'unknown-mcp';
      const mode: MCPMode = server.proxy === true ? 'proxy' : 'native';
      const sourceOrigin = text(server.source_origin) || 'unknown';
      rows.push({
        key: `${personaName}:${serverName}:runtime`,
        personaName,
        serverName,
        mode,
        // A local proxy address describes this process. If the definition
        // never reaches the agent, printing that address here is what made the
        // page read as all-green for an agent with no tools.
        activeProxyState: server.delivered_to_agent === false
          ? mcpUndeliveredState
          : server.proxy === true ? (text(server.local_proxy_addr) || 'proxy enabled; local address unavailable') : 'native/direct MCP; no cc-connect proxy',
        sourceOrigin,
        configPrecedence: sourceOrigin === 'config' ? 'config-source wins over managed artifact with same name' : 'managed artifact active unless config-source shadows it',
        lastCall: 'Unavailable: proxy tool-call audit is not exposed by management API',
        auditHealth: auditEventsCount ? `Control-plane audit rows: ${auditEventsCount}; proxy call audit health unavailable` : 'No structured proxy call audit endpoint',
        toolVisibility: `Partial: proxy_rules_count=${numberValue(server.proxy_rules_count)}; runtime schemas unavailable`,
        runtimeHref: `#/tools/tool-proxy/${encodeURIComponent(personaName)}/${encodeURIComponent(serverName)}`,
      });
    }
    for (const shadowed of getArray(asRecord(personaDetail.mcp).shadowed_managed).map(item => asRecord(item))) {
      if (!mcpRuntimeRowMatches(shadowed, matchCandidates)) continue;
      const serverName = text(shadowed.name) || mcpName || matchCandidates[0] || 'unknown-mcp';
      rows.push({
        key: `${personaName}:${serverName}:shadowed`,
        personaName,
        serverName,
        mode: 'proxy',
        activeProxyState: 'inactive: managed MCP is shadowed',
        sourceOrigin: 'managed shadowed',
        configPrecedence: text(shadowed.reason) || 'shadowed_by_config',
        lastCall: 'Unavailable while shadowed',
        auditHealth: 'Unavailable while shadowed',
        toolVisibility: 'No runtime schemas while shadowed',
        runtimeHref: `#/tools/tool-proxy/${encodeURIComponent(personaName)}/${encodeURIComponent(serverName)}`,
      });
    }
  }
  return rows;
}

function mcpMatchCandidates(mcpID: string, mcpName: string, runtimeName: string) {
  return Array.from(new Set([mcpID, mcpName, runtimeName].map(candidate => candidate.trim()).filter(Boolean)));
}

function mcpRuntimeRowMatches(row: Record<string, unknown>, matchCandidates: string[]) {
  const artifactID = text(row.artifact_id);
  const name = text(row.name);
  return Boolean((artifactID && matchCandidates.includes(artifactID)) || (name && matchCandidates.includes(name)));
}

function mcpContractGaps(mode: MCPMode, copy: ResourceManagementCopy) {
  const gaps = [
    `${copy.mutationContractGap}: publish, rollback, delete, archive, enable, and disable remain disabled unless backend exposes preview plus explicit confirmation metadata.`,
    'No MCP-specific consumers endpoint; usage is aggregated from Persona detail snapshots and is read-only.',
    'No structured proxy last-call/audit-health endpoint; runtime evidence marks this as unavailable instead of inferring it from logs.',
  ];
  if (mode === 'proxy') {
    gaps.push('No authoritative tool/schema visibility endpoint for Proxy MCP; proxy rule visibility is shown as partial evidence.');
  }
  return gaps;
}


function firstSpec(record: Record<string, unknown>): Record<string, unknown> {
  const latestSpec = parseSpec(asRecord(record.latest_version).spec);
  if (Object.keys(latestSpec).length) return latestSpec;
  return parseSpec(asRecord(record.draft).spec);
}

function draftFirstSpec(record: Record<string, unknown>): Record<string, unknown> {
  const draftSpec = parseSpec(asRecord(record.draft).spec);
  if (Object.keys(draftSpec).length) return draftSpec;
  return parseSpec(asRecord(record.latest_version).spec);
}

function parseSpec(value: unknown): Record<string, unknown> {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return asRecord(value);
}

function personaRecords(value: unknown[] | undefined): Record<string, unknown>[] {
  return (value ?? []).map(item => asRecord(item));
}

function conflictCountFrom(value: unknown) {
  const conflicts = asRecord(value).conflicts;
  if (Array.isArray(conflicts)) return conflicts.length;
  const conflictRecord = asRecord(conflicts);
  return Object.values(conflictRecord).reduce<number>((total, item) => total + getArray(item).length, 0);
}

function partialNotices(errors: Record<string, string> | undefined) {
  return Object.entries(errors ?? {}).map(([label, message]) => `${label}: ${message}`);
}

function statusTone(status: string): StatusItem['tone'] {
  if (status === 'active') return 'green';
  if (status === 'draft') return 'yellow';
  if (status === 'disabled' || status === 'archived') return 'muted';
  return 'muted';
}

function boolText(value: unknown) {
  return value === true ? 'Yes' : 'No';
}

function numberText(value: unknown) {
  const parsed = numberValue(value);
  return String(parsed);
}

function numberValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function getStrings(value: unknown) {
  return getArray(value).map(item => text(item)).filter(Boolean);
}

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

// boolValue keeps "absent" distinct from "false": a field an older snapshot
// does not carry must not be read as a definite no.
function boolValue(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function text(value: unknown) {
  return typeof value === 'string' ? value : value === undefined || value === null ? '' : String(value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
